/**
 * Almari · Wishlist via brand-URL paste.
 *
 * Paste a product URL (Myntra / Zara / H&M / Tata Cliq). A CF-Worker stub at
 * /api/og-scrape returns the og:image + product title + price. We keep the
 * stub fetch optimistic — falls back to title-from-URL parsing if the worker
 * is offline.
 *
 * Encrypted-at-rest. Local-only. ITC + intent-to-buy log.
 */
import { Storage } from '../chassis/index.ts';

interface WishlistEntry {
  readonly id: string;
  readonly ts: string;
  readonly url: string;
  readonly title: string;
  readonly brand: string | null;
  readonly price_inr: number | null;
  readonly thumb: string | null;
  readonly status: 'wishlist' | 'bought' | 'rejected';
}

const WISH_FILE = 'almari-wishlist.json';

async function load(): Promise<WishlistEntry[]> {
  return (await Storage.readFile<WishlistEntry[]>(WISH_FILE)) ?? [];
}
async function save(list: WishlistEntry[]): Promise<void> {
  await Storage.writeFile(WISH_FILE, list);
}

interface OgPayload {
  readonly title: string;
  readonly brand: string | null;
  readonly price_inr: number | null;
  readonly image: string | null;
}

async function scrape(url: string): Promise<OgPayload> {
  // Try CF-Worker stub first (will be deployed in v1.1 to /api/og-scrape)
  try {
    const r = await fetch(`/api/og-scrape?url=${encodeURIComponent(url)}`, { mode: 'cors' });
    if (r.ok) {
      const j = await r.json() as OgPayload;
      return j;
    }
  } catch { /* fall through */ }
  // Heuristic fallback — parse brand from hostname
  const host = (() => { try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return ''; } })();
  const brand = host.split('.')[0] ?? null;
  return { title: url, brand, price_inr: null, image: null };
}

export class AlmariWishlist extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #items: WishlistEntry[] = [];
  #busy = false;

  async connectedCallback(): Promise<void> {
    this.#items = await load();
    this.#render();
  }

  async #add(url: string): Promise<void> {
    if (this.#busy) return;
    if (!url.startsWith('http')) return;
    this.#busy = true; this.#render();
    try {
      const og = await scrape(url);
      const entry: WishlistEntry = {
        id: crypto.randomUUID(),
        ts: new Date().toISOString(),
        url,
        title: og.title,
        brand: og.brand,
        price_inr: og.price_inr,
        thumb: og.image,
        status: 'wishlist',
      };
      this.#items.unshift(entry);
      if (this.#items.length > 80) this.#items.length = 80;
      await save(this.#items);
    } finally {
      this.#busy = false;
      this.#render();
    }
  }

  async #setStatus(id: string, status: WishlistEntry['status']): Promise<void> {
    const idx = this.#items.findIndex((e) => e.id === id);
    if (idx < 0) return;
    this.#items[idx] = { ...this.#items[idx]!, status };
    await save(this.#items);
    this.#render();
  }

  #render(): void {
    const wishlist = this.#items.filter((e) => e.status === 'wishlist');
    const rows = wishlist.slice(0, 8).map((e) => `
      <article class="row" data-id="${e.id}">
        <div class="thumb">${e.thumb ? `<img src="${escapeHtml(e.thumb)}" alt="" loading="lazy" />` : `<span class="thumb-mark">${escapeHtml((e.brand ?? e.title).slice(0, 1).toUpperCase())}</span>`}</div>
        <div class="body">
          <div class="name">${escapeHtml(truncate(e.title, 60))}</div>
          <div class="meta">${escapeHtml(e.brand ?? '—')} · ${e.price_inr ? `₹${e.price_inr.toLocaleString('en-IN')}` : 'price unknown'}</div>
        </div>
        <div class="actions">
          <button data-action="bought" data-id="${e.id}" title="Mark bought">✓</button>
          <button data-action="rejected" data-id="${e.id}" title="Mark passed">×</button>
        </div>
      </article>
    `).join('');

    this.#shadow.innerHTML = `
<style>
:host { display: block; }
.head { padding-left: 4px; margin-bottom: 12px; display: grid; gap: 4px; }
.eyebrow { font-family: var(--mono); font-size: 9px; letter-spacing: 0.42em; text-transform: uppercase; color: var(--brass-dim); }
.title { font-family: var(--display); font-style: italic; font-size: 22px; color: var(--ink); font-weight: 600; letter-spacing: -0.005em; }
.card { background: var(--paper-card); border: 1px solid var(--rule); border-radius: 8px; padding: 14px 14px 16px; }
.input-row { display: grid; grid-template-columns: 1fr auto; gap: 8px; margin-bottom: 12px; }
input { font-family: var(--mono); font-size: 12px; padding: 12px 14px; border: 1px solid var(--rule); border-radius: 6px; background: var(--paper-up); color: var(--ink); letter-spacing: 0.04em; outline: none; }
input:focus { border-color: var(--brass-deep); background: var(--paper-card); }
button.add {
  font-family: var(--mono); font-size: 10px; letter-spacing: 0.32em; text-transform: uppercase;
  padding: 12px 16px; border-radius: 6px; background: var(--ink-deep); color: var(--paper);
  font-weight: 700; cursor: pointer; border: 0;
}
button.add[disabled] { opacity: 0.5; cursor: wait; }
.empty { font-family: var(--display); font-style: italic; color: var(--ink-faint); font-size: 16px; padding: 14px 4px; text-wrap: pretty; }
.row {
  display: grid; grid-template-columns: 48px 1fr auto; gap: 12px; align-items: center;
  padding: 10px 6px; border-top: 1px dashed var(--rule);
}
.thumb {
  width: 48px; height: 48px; border-radius: 4px; background: var(--paper-up);
  border: 1px solid var(--rule); overflow: hidden; display: grid; place-items: center;
}
.thumb img { width: 100%; height: 100%; object-fit: cover; }
.thumb-mark { font-family: var(--display); font-size: 20px; color: var(--brass-deep); font-weight: 700; }
.body { min-width: 0; display: grid; gap: 3px; }
.name { font-family: var(--display); font-size: 15px; color: var(--ink); font-weight: 600; letter-spacing: -0.005em; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.meta { font-family: var(--mono); font-size: 9.5px; letter-spacing: 0.05em; color: var(--ink-faint); text-transform: lowercase; }
.actions { display: flex; gap: 4px; }
.actions button {
  width: 26px; height: 26px; border-radius: 4px; border: 1px solid var(--rule);
  background: var(--paper-up); color: var(--ink-dim); font-size: 14px; cursor: pointer;
  font-weight: 700;
}
.actions button[data-action="bought"]:hover { background: oklch(82% 0.08 145); color: oklch(36% 0.15 145); border-color: oklch(60% 0.10 145); }
.actions button[data-action="rejected"]:hover { background: oklch(86% 0.06 22); color: oklch(48% 0.14 22); }
.bottom { font-family: var(--mono); font-size: 9px; letter-spacing: 0.22em; text-transform: uppercase; color: var(--ink-faint); margin-top: 10px; padding-top: 10px; border-top: 1px dashed var(--rule); text-align: right; }
</style>
<header class="head">
  <span class="eyebrow">Wishlist · brand-URL paste</span>
  <h3 class="title">Khwahish ko archive karo. Khareedne se pehle.</h3>
</header>
<div class="card">
  <form class="input-row" id="form">
    <input id="url" type="url" placeholder="myntra.com/...   zara.com/...   tatacliq.com/..." autocomplete="off" />
    <button type="submit" class="add" ${this.#busy ? 'disabled' : ''}>${this.#busy ? '…' : 'Add'}</button>
  </form>
  ${wishlist.length === 0 ? '<p class="empty">Khaali wishlist. Aapne abhi tak kuch nahi rakha.</p>' : rows}
  <div class="bottom">${this.#items.filter((e) => e.status === 'bought').length} bought · ${this.#items.filter((e) => e.status === 'rejected').length} passed</div>
</div>
`;

    const form = this.#shadow.getElementById('form') as HTMLFormElement | null;
    const input = this.#shadow.getElementById('url') as HTMLInputElement | null;
    form?.addEventListener('submit', (e) => {
      e.preventDefault();
      const v = (input?.value ?? '').trim();
      if (!v) return;
      if (input) input.value = '';
      void this.#add(v);
    });
    this.#shadow.querySelectorAll<HTMLButtonElement>('.actions button').forEach((b) => {
      b.addEventListener('click', () => {
        const id = b.dataset.id ?? '';
        const action = b.dataset.action as WishlistEntry['status'];
        void this.#setStatus(id, action);
      });
    });
  }
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

customElements.define('almari-wishlist', AlmariWishlist);
